'use client';

import type {PropsWithChildren, ReactNode} from 'react';
import clsx from 'clsx';
import {getImageProps} from 'next/image';

import {Alert} from '@/modules/finkn/components/alert';
import {Badge} from '@/modules/finkn/components/badge';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {getBackgroundImage} from '@/modules/page/components/page-head/getBackgroundImage';

import {resolveVariant} from '../../lib/resolveVariant';
import type {FieldOfArea} from '../../sanity/complaint-guide/constants';
import {useComplaintGuide} from '../guide-context';

import {WhyDoWeAskDialog} from './WhyDoWeAskDialog';

type QuestionVariant = {
	fieldOfArea: FieldOfArea;
	title?: string;
	whyDoWeAsk?: ReactNode;
};

type AnswerVariant = {
	fieldOfArea: FieldOfArea;
	title?: string;
	info?: {
		heading?: string;
		description?: ReactNode;
	};
};

export type ComplaintGuideQuestion = {
	_key: string;
	_type: 'question';
	title: string;
	answers: Answer[];
	questionId: string;
	whyDoWeAsk?: ReactNode;
	variants?: QuestionVariant[];
};

type Answer = {
	_key: string;
	_type: 'negativeAnswer' | 'positiveAnswer';
	title: string;
	info?: {
		heading?: string;
		description?: ReactNode;
	};
	positiveAnswerId?: string;
	fieldOfArea?: FieldOfArea;
	variants?: AnswerVariant[];
};

export default function ComplaintGuide({children}: PropsWithChildren) {
	const {
		currentQuestion,
		answers,
		setAnswer,
		showResult,
		currentAnswer,
		currentStep,
		questions,
		activeFieldOfArea,
	} = useComplaintGuide();

	const {
		props: {srcSet},
	} = getImageProps({
		alt: '',
		width: 1960,
		height: 1024,
		src: '/images/finkn-pattern.svg',
	});

	const backgroundImage = getBackgroundImage(srcSet);

	if (showResult) {
		return (
			<Container>
				<div
					className={clsx(
						'md:flex',
						'gap-10',
						'items-center',
						'min-h-[calc(100dvh-161px)]',
						'md:min-h-[calc(100dvh-216px)]',
						'py-10',
						'justify-between',
					)}
				>
					<div
						className={clsx(
							'flex',
							'flex-1',
							'flex-col',
							'items-start',
							'md:max-w-[535px]',
							'max-md:mb-6',
						)}
					>
						<Badge variant="outline" className={clsx('mb-3')}>
							Oppsummering
						</Badge>
						<Heading
							size="2xl"
							className={clsx(
								'mb-4',
								'md:mb-10',
								'max-md:text-[21px]',
							)}
						>
							Det ser ut som om vi kan behandle klagen din!
						</Heading>
					</div>
					<ul
						className={clsx(
							'md:max-w-[650px]',
							'flex-1',
							'grid',
							'gap-[14px]',
						)}
						aria-label="Dine svar"
					>
						{answers.map((answer) => (
							<li
								key={answer._key}
								className={clsx(
									'border-b',
									'border-grey-border',
									'pb-[14px]',
									'flex',
									'flex-col',
								)}
							>
								<Heading asChild size="sm" spacing>
									<strong>{answer.questionTitle}</strong>
								</Heading>
								<div>
									<span className="font-semibold">Svar:</span>{' '}
									{answer.title}
								</div>
							</li>
						))}
					</ul>
				</div>
			</Container>
		);
	}

	const questionTitle = currentQuestion
		? resolveVariant(
				activeFieldOfArea,
				currentQuestion.variants,
				'title',
				currentQuestion.title,
			)
		: '';

	const questionWhyDoWeAsk = currentQuestion
		? resolveVariant(
				activeFieldOfArea,
				currentQuestion.variants,
				'whyDoWeAsk',
				currentQuestion.whyDoWeAsk,
			)
		: null;

	if (!currentQuestion) {
		return (
			<div
				style={{
					backgroundImage: `linear-gradient(to right, transparent, #FAFBFC), ${backgroundImage}`,
				}}
				className={clsx('bg-cover', 'bg-right')}
			>
				{children}
			</div>
		);
	}

	return (
		<Container>
			<div
				className={clsx(
					'md:flex',
					'flex-row',
					'gap-10',
					'items-center',
					'justify-between',
					'min-h-[calc(100dvh-161px)]',
					'md:min-h-[calc(100dvh-216px)]',
					'py-10',
				)}
			>
				<div
					className={clsx(
						'flex',
						'flex-1',
						'flex-col',
						'items-start',
						'md:max-w-[535px]',
						'max-md:mb-6',
					)}
				>
					<Badge variant="outline" className={clsx('mb-3')}>
						Spørsmål {currentStep + 1} av {questions.length}
					</Badge>
					<Heading
						asChild
						size="2xl"
						className={clsx(
							'mb-4',
							'md:mb-10',
							'max-md:text-[21px]',
						)}
						id={currentQuestion._key}
					>
						<span>{questionTitle}</span>
					</Heading>
					{questionWhyDoWeAsk ? (
						<WhyDoWeAskDialog>
							{questionWhyDoWeAsk}
						</WhyDoWeAskDialog>
					) : null}
				</div>
				<div
					className={clsx(
						'md:max-w-[650px]',
						'flex-1',
						'grid',
						'gap-4',
					)}
					aria-describedby={currentQuestion._key}
				>
					{currentQuestion.answers.map((answer) => {
						const answerTitle = resolveVariant(
							activeFieldOfArea,
							answer.variants,
							'title',
							answer.title,
						);
						const answerInfo = resolveVariant(
							activeFieldOfArea,
							answer.variants,
							'info',
							answer.info,
						);
						return (
							<div
								key={answer._key}
								className={clsx('grid', 'gap-4')}
							>
								<label
									className={clsx(
										'flex',
										'items-center',
										'gap-2',
									)}
								>
									<input
										type="radio"
										name={currentQuestion._key}
										value={answer._key}
										checked={
											currentAnswer?._key === answer._key
										}
										className={clsx(
											'size-[21px]',
											'border',
											'border-grey-45',
											'checked:bg-primary-25',
										)}
										onChange={() =>
											setAnswer({
												...answer,
												title: answerTitle,
												questionKey:
													currentQuestion._key,
												questionTitle,
												questionId:
													currentQuestion?.questionId,
											})
										}
									/>
									{answerTitle}
								</label>

								{currentAnswer?._key === answer._key &&
									(answerInfo?.heading ||
										answerInfo?.description) && (
										<Alert
											severity={
												answer._type ===
												'negativeAnswer'
													? 'error'
													: 'info'
											}
											title={answerInfo?.heading}
										>
											{answerInfo?.description}
										</Alert>
									)}
							</div>
						);
					})}
				</div>
			</div>
		</Container>
	);
}
